import { useState } from 'react'
import Typography from '../../../../components/common/Typography'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import { InlineContainer, ListItemTextBase } from '../../../../components/common/Containers'
import { RouterLink } from '../../../../components/common/Buttons'
import { Avatar } from '../../../../components/common/Cards'
import { IconButton } from '@material-ui/core'
import { formatPrettyUnixDate } from '../../../../utils/dateUtils'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import { DATE_TIME_PRETTY_FORMAT } from '../../../../constants'
import ConfirmDialog from '../../../../components/generics/Confirm/Dialog'
import useBooleanHandlers from '../../../../hooks/useBooleanHandlers'
import { useTrans } from '../../../../services/i18n'
import { selectUserPermissions, isEnabled } from '../../../../selectors'
import { useSelector } from 'react-redux'
import usePopover from '../../../../hooks/usePopover'
import {
  ContextMenuListItem,
  Popover,
} from '../../../../components/common/Dialogs/Popover'
import { useAlertContext } from '../../../../contexts/AlertContext'

const Item = ({ detail, user, deleteUser, getDetail, changeUserAccountStatus }) => {
  const [openRemove, onOpenRemove, onCloseRemove] = useBooleanHandlers()
  const [pendingRemove, setPendingRemove] = useState(false)
  const [pendingStatus, setPendingStatus] = useState(false)
  const [anchorEl, onOpenPopover, onClosePopover] = usePopover()
  const { trans } = useTrans()
  const { successAlert, errorAlert } = useAlertContext()
  const permissions = useSelector(selectUserPermissions)

  const onDeleteUser = () => {
    setPendingRemove(true)

    deleteUser(user)
      .then(() => {
        onCloseRemove()
        getDetail({ organizationId: detail.id })
      })
      .catch(() => {})
      .finally(() => {
        setPendingRemove(false)
      })
  }

  const onChangeUserAccountStatus = () => {
    if (changeUserAccountStatus) {
      setPendingStatus(true)
      changeUserAccountStatus({
        organizationUserId: user.organizationUserId,
        disabled: user.disabled ? false : true,
      })
        .then(() => {
          successAlert(
            `${user.name} ${user.familyName} has been ${user.disabled ? 'enabled' : 'disabled'}`
          )
          getDetail({ organizationId: detail.id })
        })
        .catch(() => {
          errorAlert('Error')
        })
        .finally(() => {
          setPendingStatus(false)
        })
    }
  }

  return (
    <>
      <TableRow>
        <TableCell align="left">
          <InlineContainer spacing={1}>
            <Avatar
              bgColor="gray-second"
              variantAvatar="rounded"
              size={40}
              value={`${user.name} ${user.familyName}`}
            />
            <Typography
              weight="500"
              to={`/usfcr-staff/${user.userId}`}
              variant="h6"
              component={RouterLink}
            >
              {user.name} {user.familyName}
            </Typography>
          </InlineContainer>
        </TableCell>
        <TableCell align="left">
          <Typography variant="inherit" fontFamily="lexend">
            {user.email}
          </Typography>
        </TableCell>
        <TableCell align="left">
          <Typography variant="inherit" fontFamily="lexend">
            {user.lastLogin
              ? formatPrettyUnixDate(user.lastLogin, DATE_TIME_PRETTY_FORMAT)
              : ''}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            weight="600"
            noWrap
            color={user.disabled ? 'secondary' : 'success'}
          >
            {user.disabled ? 'Disabled' : 'Enabled'}
          </Typography>
        </TableCell>
        {(isEnabled(permissions, 'organization', 'usfcr-staff', 'edit') ||
          isEnabled(permissions, 'organization', 'usfcr-staff', 'delete')) && (
          <TableCell align="right">
            <IconButton variant={'contained'} size="small" onClick={onOpenPopover}>
              <MoreHorizIcon />
            </IconButton>
          </TableCell>
        )}
      </TableRow>

      <Popover
        id={'id-users-actions'}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClosePopover}
      >
        {isEnabled(permissions, 'organization', 'usfcr-staff') && (
          <ContextMenuListItem
            button
            disabled={pendingStatus}
            onClick={onChangeUserAccountStatus}
            onClickCB={onClosePopover}
          >
            <ListItemTextBase>
              {user.disabled
                ? trans('enable-user-organization')
                : trans('disable-user-organization')}
            </ListItemTextBase>
          </ContextMenuListItem>
        )}
        {isEnabled(permissions, 'organization', 'usfcr-staff', 'delete') && (
          <ContextMenuListItem
            button
            onClick={onOpenRemove}
            onClickCB={onClosePopover}
          >
            <ListItemTextBase>{trans('delete')}</ListItemTextBase>
          </ContextMenuListItem>
        )}
      </Popover>

      <ConfirmDialog
        open={openRemove}
        onClose={onCloseRemove}
        onConfirm={onDeleteUser}
        pending={pendingRemove}
        title={trans('remove-user-usfcr-staff')}
        text={'Are you sure you want to remove this USFCR Staff member from this organization?'}
      >
        <Typography color="hint">
          {user.name} {user.familyName}
        </Typography>
      </ConfirmDialog>
    </>
  )
}

export default Item